// 22 Sep 2026 · Offline-Status + letzter Sync-Zeitstempel.
import { createContext, useContext, useEffect, useState } from "react";
import { getMeta } from "./offlineDb";
import api from "./api";
import { toast } from "sonner";

const STALE_MS = 7 * 24 * 60 * 60 * 1000; // 7 Tage

const OfflineContext = createContext({ online: true, lastSync: null, isStale: false });

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let mounted = true;
    getMeta("lastSync").then((v) => { if (mounted && v) setLastSync(v); });

    const onOnline = () => {
      setOnline(true);
      // 23 Sep 2026 · Auto-Sync beim Wiederverbinden: ein garantierter GET
      // aktualisiert Cache + „Stand"-Zeitstempel sofort (Response-Interceptor
      // feuert `wb:sync`), und `wb:reconnect` weist die Datenquellen an, sofort
      // frisch zu laden. Nach erfolgreichem Sync eine konkrete Bestätigung
      // „Aktualisiert: HH:MM" statt eines vagen „Wieder online".
      api.get("/auth/me").then(() => {
        try {
          const d = new Date();
          const hh = String(d.getHours()).padStart(2, "0");
          const mm = String(d.getMinutes()).padStart(2, "0");
          toast.success(`Aktualisiert: ${hh}:${mm}`);
        } catch { /* ignore */ }
      }).catch(() => {});
      try { window.dispatchEvent(new CustomEvent("wb:reconnect")); } catch { /* ignore */ }
    };
    const onOffline = () => setOnline(false);
    const onSync = (e) => setLastSync((e && e.detail && e.detail.ts) || Date.now());
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("wb:sync", onSync);

    // Minütlicher Tick, damit die „älter als 7 Tage"-Grenze auch bei lange
    // geöffneter App irgendwann greift, ohne Interaktion.
    const tick = setInterval(() => setNow(Date.now()), 60 * 1000);

    return () => {
      mounted = false;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("wb:sync", onSync);
      clearInterval(tick);
    };
  }, []);

  const isStale = !!lastSync && (now - lastSync > STALE_MS);
  return (
    <OfflineContext.Provider value={{ online, lastSync, isStale }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  return useContext(OfflineContext);
}

export function useOnline() {
  return useContext(OfflineContext).online;
}
